/** Types shared between the Bun server and the React client. */

export const BUCKETS = ["season", "year", "next_year", "someday"] as const;
export type Bucket = (typeof BUCKETS)[number];

export const BUCKET_LABELS: Record<Bucket, string> = {
  season: "This season",
  year: "This year",
  next_year: "Next year",
  someday: "Someday",
};

export const STATUSES = ["idea", "planned", "in_progress", "done"] as const;
export type Status = (typeof STATUSES)[number];

export const STATUS_LABELS: Record<Status, string> = {
  idea: "Idea",
  planned: "Planned",
  in_progress: "In progress",
  done: "Done",
};

export const PHOTO_KINDS = ["normal", "before", "after"] as const;
export type PhotoKind = (typeof PHOTO_KINDS)[number];

export const ROLES = ["adult", "kid"] as const;
export type Role = (typeof ROLES)[number];

export interface Family {
  id: string;
  slug: string;
  name: string;
  created_at: string;
}

export interface Person {
  id: string;
  family_id: string;
  name: string;
  username: string;
  role: Role;
  created_at: string;
}

export interface Photo {
  id: string;
  project_id: string;
  uploaded_by: string;
  kind: PhotoKind;
  created_at: string;
}

export interface Comment {
  id: string;
  project_id: string;
  person_id: string;
  person_name: string;
  body_md: string;
  created_at: string;
}

export interface ProjectRef {
  id: string;
  title: string;
  status: Status;
}

export interface Project {
  id: string;
  family_id: string;
  title: string;
  area: string;
  bucket: Bucket;
  status: Status;
  notes_md: string;
  created_by: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  photo_count: number;
  comment_count: number;
  /**
   * Position within its bucket in the one shared order everyone sees
   * (lower sorts first). Only meaningful relative to other projects in the
   * same bucket.
   */
  sort_order: number;
  /** Projects that must finish before this one can start. */
  blocked_by: ProjectRef[];
}

export interface ProjectDetail extends Project {
  photos: Photo[];
  comments: Comment[];
  /** Projects waiting on this one. */
  blocks: ProjectRef[];
}

export const AUDIT_ACTIONS = [
  "created",
  "updated",
  "status_changed",
  "bucket_changed",
  "photo_added",
  "photo_removed",
  "comment_added",
  "dependency_added",
  "dependency_removed",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/**
 * Small, human-renderable payload for an audit entry. Field-diff entries
 * carry `field`/`from`/`to`; other action types stash whatever minimal
 * context the UI needs to phrase the entry (an excerpt, a blocker's title,
 * a photo kind, ...). Deliberately loose — this is display data, not a
 * contract anything else reads back.
 */
export interface AuditDetail {
  field?: string;
  from?: string | null;
  to?: string | null;
  [key: string]: unknown;
}

export interface AuditEntry {
  id: string;
  project_id: string;
  /** Null for system-attributed entries (e.g. backfill with no matching person). */
  person_id: string | null;
  person_name: string | null;
  action: AuditAction;
  detail: AuditDetail;
  created_at: string;
}

export interface SessionInfo {
  person: Person;
  family: Family;
}

/** What the (unauthenticated) login screen's person picker gets — nothing more. */
export interface PersonForLogin {
  name: string;
  username: string;
}
